/** Typed REST client for §6.1. Same origin, cookie `vc_sid` (HttpOnly) sent automatically. */
import type {
  ConfigResponse,
  ErrorResponse,
  LinkCodeRequest,
  LinkResponse,
  MeResponse,
} from './protocol';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message?: string,
  ) {
    super(message ?? code);
    this.name = 'ApiError';
  }
}

type FetchLike = typeof fetch;

async function request<T>(fetchImpl: FetchLike, method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetchImpl(path, {
      method,
      credentials: 'same-origin',
      headers: body !== undefined ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    throw new ApiError(0, 'NETWORK', e instanceof Error ? e.message : String(e));
  }
  if (res.status === 204) return undefined as T;
  let json: unknown = undefined;
  const text = await res.text();
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = undefined;
    }
  }
  if (!res.ok) {
    const err = (json ?? {}) as Partial<ErrorResponse> & { code?: string };
    const code = err.error ?? err.code ?? (res.status === 401 ? 'UNAUTHORIZED' : res.status === 429 ? 'RATE_LIMITED' : `HTTP_${res.status}`);
    throw new ApiError(res.status, code, err.message);
  }
  return json as T;
}

/** Normalizes a short code: uppercase, strips separators, Crockford aliases (O->0, I/L->1). */
export function normalizeLinkCode(input: string): string {
  return input
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '')
    .replace(/O/g, '0')
    .replace(/[IL]/g, '1');
}

/** Crockford base32 alphabet (no I, L, O, U). */
export const CROCKFORD_RE = /^[0-9ABCDEFGHJKMNPQRSTVWXYZ]{8}$/;

/** `K7QF2MXR` -> `K7QF-2MXR`. */
export function formatLinkCode(normalized: string): string {
  return normalized.length > 4 ? `${normalized.slice(0, 4)}-${normalized.slice(4, 8)}` : normalized;
}

export function createApi(fetchImpl: FetchLike = (...args) => fetch(...args)) {
  return {
    /** Redeems the short code of `/voice` (typed, or carried by the chat link). */
    link(code: string) {
      const body: LinkCodeRequest = { code: normalizeLinkCode(code) };
      return request<LinkResponse>(fetchImpl, 'POST', '/api/link', body);
    },
    me() {
      return request<MeResponse>(fetchImpl, 'GET', '/api/me');
    },
    logout() {
      return request<void>(fetchImpl, 'POST', '/api/logout', {});
    },
    config() {
      return request<ConfigResponse>(fetchImpl, 'GET', '/api/config');
    },
  };
}

export type Api = ReturnType<typeof createApi>;

export const api = createApi();

/** Italian, user-facing explanation of a link error. */
export function describeLinkError(e: unknown): string {
  if (!(e instanceof ApiError)) return 'Qualcosa è andato storto. Riprova tra poco.';
  switch (e.code) {
    case 'INVALID_OR_EXPIRED':
      return 'Codice o link non valido, oppure scaduto. Scrivi di nuovo /voice in gioco per ottenerne uno nuovo.';
    case 'RATE_LIMITED':
      return 'Troppi tentativi. Aspetta un minuto e riprova.';
    case 'NETWORK':
      return 'Impossibile raggiungere il server. Controlla la connessione.';
    default:
      if (e.status === 503) return 'Il servizio è momentaneamente sovraccarico. Riprova tra poco.';
      if (e.status >= 500) return 'Errore del server. Riprova tra poco.';
      return 'Richiesta non valida. Scrivi di nuovo /voice in gioco.';
  }
}
